import axios from 'axios';
import moment from 'moment-timezone';
import LogRocket from 'logrocket';
import { extractApiError } from '@/utils/apiError';
import { takePrefetchedForm } from '@/utils/formPrefetch';
import { invalidateSendCatalog } from '@/utils/sendPanelCatalog';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

export default {
    namespaced: true,
    state: {
        forms: [],
        // Transient hand-off slot for the "Create with AI" flow: the generated
        // (unsaved) ConfigData draft, consumed once by CreateForm.vue then cleared.
        aiDraft: null,
    },

    mutations: {
        setAiDraft(state, draft) {
            state.aiDraft = draft;
        },
        clearAiDraft(state) {
            state.aiDraft = null;
        },
    },

    getters: {
    },

    actions: {

        async getForm({dispatch}, payload) {
            // payload.silent (optional, see getForms): skip every alert/* dispatch.
            const silent = !!(payload && payload.silent);
            try {
                if (!silent) dispatch('alert/setLoading', true, {root: true});

                // Prefetched by public/index.html alongside the bundle download so the
                // patient form's fields exist before the `load` event. Single-use and
                // id-matched; a miss falls through to the normal request.
                const prefetched = await takePrefetchedForm(payload.formIDForWeb);
                if (prefetched) {
                    if (!silent) dispatch('alert/setLoaded', true, {root: true});
                    return prefetched;
                }

                let response = await axios.get(`/get-form/${payload.formIDForWeb}`, payload, { crossdomain: true });
                if (!silent) dispatch('alert/setLoaded', true, {root: true});
                return response;
            } catch (err) {
                console.log('Error getting form:', err);
                if (!silent) dispatch('alert/setErrored', true, {root: true});
                if (!silent) dispatch('alert/setMessage', err, {root: true});
            }
        },

        async getForms({dispatch}, payload) {
            // payload: { siteID, [includeConfigData] }
            //   includeConfigData: optional boolean. Defaults to true on the
            //     backend for backward compat (bulkCopyForms relies on it).
            //     FormsHome passes `false` to avoid shipping MB of config
            //     blobs it doesn't need — the backend returns derived scalars
            //     (SupportedLanguages, SpanishEnabled, FieldCount) either way.
            //   silent: optional boolean (absent => exactly the old behaviour). true skips every
            //     alert/* dispatch: the caller shows its own loading / error + Retry. The builder's
            //     "Copy a section from another form" modal needs it — alert/setLoading raises
            //     EditForm's "Saving form" CommonOverlay (z-index 9999999) over the modal. Errors
            //     still resolve undefined, so a silent caller treats a non-result as a failure.
            const silent = !!(payload && payload.silent);
            let forms = [];
            try {
                if (!silent) dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/get-forms', payload, { crossdomain: true });
                if(response.status === 200) {
                    forms = response.data;
                }
                if (!silent) dispatch('alert/setLoaded', true, {root: true});
                return forms;
            } catch (err) {
                console.log('Error getting forms:', err);
                if (!silent) dispatch('alert/setErrored', true, {root: true});
                if (!silent) dispatch('alert/setMessage', err, {root: true});
            }
        },

        // Procedures on the patient's current/next appointment, grouped by the
        // consent form each one requires (Get Form QR modal's procedure table).
        // payload: { siteID, patNum }
        //   -> { appointments: [{ aptNum, aptDateTime, aptStatus, groups, unmapped }],
        //        mappingsConfigured }
        // Degrades quietly: the modal's normal form list is unaffected if this
        // fails, so a failure returns null instead of red-toasting the whole modal.
        async getAppointmentConsentForms(_ctx, payload) {
            try {
                let response = await axios.post('/get-appointment-consent-forms', payload, { crossdomain: true });
                return response.data;
            } catch (err) {
                console.log('Error getting appointment consent forms:', err);
                return null;
            }
        },

        // --- Forms Update Reminders (medical-history update nudge) ---
        async getFormsReminderSettings({dispatch}, payload) {
            // payload: { siteID } -> { success, data: { settings, forms } }
            try {
                let response = await axios.post('/get-forms-reminder-settings', payload, { crossdomain: true });
                return response.data;
            } catch (err) {
                console.log('Error getting forms reminder settings:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async saveFormsReminderSettings({dispatch}, payload) {
            // payload: { siteID, settings }
            try {
                let response = await axios.post('/save-forms-reminder-settings', payload, { crossdomain: true });
                return response;
            } catch (err) {
                console.log('Error saving forms reminder settings:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        async getFormUpdateStatus({dispatch}, payload) {
            // payload: { siteID, patNum } -> { success, data: { enabled, isOverdue, ... } }
            // Best-effort (Patient Profile signal) — swallow errors so it never blocks the page.
            try {
                let response = await axios.post('/get-form-update-status', payload, { crossdomain: true });
                return response.data;
            } catch (err) {
                console.log('Error getting form update status:', err);
                return null;
            }
        },

        async getFormCategories({dispatch}, payload) {
            try {                
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/get-form-categories', payload, { crossdomain: true });                
                dispatch('alert/setLoaded', true, {root: true});                
                return response;
            } catch (err) {
                console.log('Error getting form categories:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async saveFormPdf({dispatch}, payload) { 
            //console.dir(payload);
            try {
                dispatch('alert/setLoading', true, {root: true});

                var formData = new FormData();

                let _this = this;
                let b,k;

                axios.defaults.headers.post['Content-Type'] = 'application/json';
                return axios
                  .post('/requestUploadURL', payload.params)
                  .then(function(response){
                    //console.log('Received response:', response);
                    return response;
                  })
                  .then(function(j) {
                    //console.log('j', j);
                    b = j.data.fields['bucket'];
                    k = j.data.fields['key'];
                    Object.keys(j.data.fields).forEach(key => {
                      formData.append(key, j.data.fields[key]);
                    });
                    formData.append("file", payload.file);

                    return axios.post(j.data.url, formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data'
                      }
                    });
                  })
                  .then(res => {
                    console.log("res", res);                    
                    dispatch('alert/setLoaded', true, {root: true});        
                    dispatch('alert/setMessage', 'PDF Uploaded successfully!', {root: true});
                    dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                    let p = {
                        siteID: payload.params.siteID,
                        pdfCategory: payload.params.pdfCategory,
                        pdfFileName: payload.params.name,
                        patNum: payload.params.patNum,
                        b: b,
                        k: k,
                    }
                    console.log('p', p);
                    dispatch('forms/sendFormToOD', p, {root: true});
                  })
                  .catch(err => {                    
                    console.error("Error while uploading a form pdf:", err);
                    _this.$toast.error('Error while uploading PDF. Please try again later');                    
                  });      
            } catch (err) {
                console.log('Error saving form pdf:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        

        async startConvertFormToPDF({dispatch}, payload) {
            dispatch('alert/setLoading', true, {root: true});
            axios.defaults.headers.post['Content-Type'] = 'application/json';
            try {
                let res = await axios.post('/start-convert-form-to-pdf', payload);            
                dispatch('alert/setLoaded', true, {root: true});
                dispatch('alert/setMessage', 'Your request is submitted. Please wait few minutes for the convered PDF to arrive on your machine', {root: true});
                dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                return res;
            } catch(err) {
                console.log('Error starting converting form to pdf:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        /* technically this is not needed as the function above will do this job
        async convertFormToPDF({dispatch}, payload) {
            dispatch('alert/setLoading', true, {root: true});
            axios.defaults.headers.post['Content-Type'] = 'application/json';
            try {
                let res = await axios.post('/convert-form-to-pdf', payload);            
                dispatch('alert/setLoaded', true, {root: true});
                dispatch('alert/setMessage', 'PDF sent! Please wait few minutes for it to arrive', {root: true});
                dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                return res;
            } catch(err) {
                console.log('Error converting form to pdf:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },
        */

        async sendFormToOD({dispatch}, payload) {            
            try {                
                let response = await axios.post(`/send-form-to-od`, payload, { crossdomain: true });
                console.log('sendform to od msg sent to queue');
            } catch (err) {
                console.log('Error saving form to OD:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async getFormSubmission({dispatch}, payload) {            
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.get(`/get-form-submission/${payload.formSubmissionIDForWeb}`, payload, { crossdomain: true });
                dispatch('alert/setLoaded', true, {root: true});                
                return response;
            } catch (err) {
                console.log('Error getting form:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async getFormSubmissionFromFormIDForWeb({dispatch}, payload) {            
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post(`/get-form-submission-from-formid`, payload, { crossdomain: true });
                dispatch('alert/setLoaded', true, {root: true});                
                return response;
            } catch (err) {
                console.log('Error getting form from FormIDForWeb:', err);                
            }
        },

        async getFormSubmissions({dispatch}, payload) {      
            let submissions = [];      
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post(`/get-form-submissions`, payload, { crossdomain: true });                
                if(response.status === 200) {
                    // Handle new paginated response structure
                    // The API now returns: { data: { submissions: [...], pagination: {...} } }
                    // Or old structure: { data: [...] } for backward compatibility
                    // common.createSuccessResponse wraps it as: { data: { submissions: [...], pagination: {...} } }
                    let responseData = response.data;
                    
                    // Unwrap if wrapped by common.createSuccessResponse
                    if (responseData && responseData.data) {
                        responseData = responseData.data;
                    }
                    
                    // Check if it's the new paginated structure
                    if (responseData && responseData.submissions && Array.isArray(responseData.submissions)) {
                        // New structure - extract submissions array
                        submissions = responseData.submissions;
                    } else if (Array.isArray(responseData)) {
                        // Old structure - direct array
                        submissions = responseData;
                    } else {
                        // Fallback to empty array
                        console.warn('[getFormSubmissions] Unexpected response structure:', responseData);
                        submissions = [];
                    }
                }
                dispatch('alert/setLoaded', true, {root: true});                
                return submissions;
            } catch (err) {
                console.log('Error getting form submissions:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                return []; // Return empty array on error
            }
        },

        // payload { siteID, formIDForWeb, patNum, linkMode? } — linkMode ('new' | 'existing', generic links
        // only) rides through untouched; see src/pages/Forms/lib/linkMode.js.
        async getFormUrl({dispatch}, payload) {                      
            try {
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post(`/get-form-url`, payload, { crossdomain: true });
                dispatch('alert/setLoaded2', true, {root: true}); 
                /*
                dispatch('alert/setMessage', `Your Form URL is: ${response.data}`, {root: true});
                dispatch('alert/setDismissSuccessCountDown', 30, {root: true});               
                */
                return response;
            } catch (err) {
                console.log('Error getting form url:', err);
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw err;
            }
        },

        /*
        // get urls for multiple forms (used in patient profile sidebar when sending forms)
        async getFormUrlForForms({dispatch}, payload) {                      
            try {
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post(`/get-form-urls-for-forms`, payload, { crossdomain: true });                
                dispatch('alert/setLoaded2', true, {root: true});                
                return response;
            } catch (err) {
                console.log('Error getting form urls for forms:', err);
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw err;
            }
        },
        */

        async linkSubmissionToPatient({dispatch}, payload) {                      
            try {
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post(`/link-submission-to-patient`, payload, { crossdomain: true });                
                dispatch('alert/setLoaded2', true, {root: true});                
                dispatch('alert/setMessage', 'Submission linked successfully!', {root: true});
                dispatch('alert/setDismissSuccessCountDown', 3, {root: true});           
                return response;
            } catch (err) {
                console.log('Error linking form submission to a patient:', err);
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw err;
            }
        },

        async verifyForm({dispatch}, payload) {                      
            let response;            

            try {
                dispatch('alert/setLoading', true, {root: true});
                response = await axios.post(`/verify-form`, payload, { 
                    crossdomain: true,                    
                });                
                dispatch('alert/setLoaded', true, {root: true});     
                //dispatch('alert/setMessage', 'Form Verified Successfully!', {root: true});
                //dispatch('alert/setDismissSuccessCountDown', 3, {root: true});                
                return response;
            } catch (err) {                
                dispatch('alert/setLoaded', true, {root: true});
                throw err;
                //dispatch('alert/setErrored', true, {root: true});
                //dispatch('alert/setMessage', response, {root: true});                
            }            
        },

        // "Existing patients" website link — public IdentifyPatient page, step 1.
        // payload { urlID, dob, dateFormat, firstName, lastName, lang } → 200 { challengeId, channel, maskedDestination }.
        // Errors carry a JSON `code` (BAD_LINK / NOT_FOUND / NO_CONTACT / RATE_LIMITED / SEND_FAILED / UNAVAILABLE).
        // No global alert/toast dispatch and NO logging here: the page shows its own copy, and the payload is PHI.
        async identifyPatientForForm(context, payload) {
            return axios.post('/identify-patient-for-form', payload, { crossdomain: true });
        },

        // Step 2: payload { challengeId, code } → 200 { urlID } (the PATIENT-SPECIFIC short-link id),
        // or { challengeId, resend: true } → 200 { challengeId, channel, maskedDestination, resent: true }.
        // Errors: 400 WRONG_CODE (+ attemptsLeft), 410 EXPIRED, 429 RESEND_LIMIT.
        async verifyIdentifyCode(context, payload) {
            return axios.post('/verify-identify-code', payload, { crossdomain: true });
        },

        async saveForm({dispatch}, payload) {            
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/save-form', payload, { crossdomain: true });
                invalidateSendCatalog('forms');
                dispatch('alert/setLoaded', true, {root: true});
                //dispatch('alert/setMessage', 'Form saved successfully!', {root: true});
                //dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                return response;
            } catch (err) {
                console.log('Error saving forms:', err);
                //dispatch('alert/setErrored', true, {root: true});
                //dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        // Create-with-AI: ask the backend to generate a draft ConfigData from a
        // natural-language description. Returns the axios response; the caller stashes
        // the draft via `setAiDraft` and hands it to the FormBuilder (UNSAVED).
        async generateForm({dispatch}, payload) {
            // payload: { siteID, prompt, formType }
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/generate-form', payload, { crossdomain: true });
                dispatch('alert/setLoaded', true, {root: true});
                return response;
            } catch (err) {
                console.log('Error generating form:', err);
                dispatch('alert/setLoaded', true, {root: true});
                throw err;
            }
        },

        // Edit-with-AI: send the current form + a natural-language instruction; the
        // backend returns { configData (edited, ids preserved), changes, warnings }.
        // The caller previews `changes` and only swaps the builder config on Apply.
        async editForm({dispatch}, payload) {
            // payload: { siteID, instruction, configData }
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/edit-form', payload, { crossdomain: true });
                dispatch('alert/setLoaded', true, {root: true});
                return response;
            } catch (err) {
                console.log('Error editing form:', err);
                dispatch('alert/setLoaded', true, {root: true});
                throw err;
            }
        },

        async saveFormSubmission({dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/save-form-submission', payload, { crossdomain: true });
                dispatch('alert/setLoaded', true, {root: true});
                //dispatch('alert/setMessage', 'Form submitted successfully!', {root: true});
                //dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                return response;
            } catch (err) {
                throw err;
                /*
                console.log('Error submitting form:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                */
            }
        },

        async checkFormReferences({dispatch}, payload) {
            try {
                let response = await axios.get(`/check-form-references/${payload.formIDForWeb}`, { crossdomain: true });
                return response.data;
            } catch (err) {
                console.log('Error checking form references:', err);
                return { references: [] };
            }
        },

        async deleteForm({dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/delete-form', payload, { crossdomain: true });
                invalidateSendCatalog('forms');
                dispatch('alert/setLoaded', true, {root: true});
                //dispatch('alert/setMessage', 'Form deleted successfully! Please wait...', {root: true});
                //dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                return response;
            } catch (err) {
                console.log('Error deleting form:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        async togglePinForm({dispatch}, payload) {
            try {
                let response = await axios.post('/toggle-pin-form', payload, { crossdomain: true });
                return response;
            } catch (err) {
                console.log('Error toggling pin form:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        async togglePinSubmission({dispatch}, payload) {
            try {
                let response = await axios.post('/toggle-pin-submission', payload, { crossdomain: true });
                return response;
            } catch (err) {
                console.log('Error toggling pin submission:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },


        async removeFormAttachment({dispatch}, payload) {            
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/remove-form-attachment', payload, { crossdomain: true });
                dispatch('alert/setLoaded', true, {root: true});
                dispatch('alert/setMessage', 'Attachment removed successfully!', {root: true});
                dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                return response;
            } catch (err) {
                console.log('Error removing form attachment file:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },
        

        async uploadDocs({dispatch}, payload) {
            console.log('in upload docs', payload);
            LogRocket.log(`in upload docs, received payload: ${JSON.stringify(payload)}`);
            try {
                //dispatch('alert/setLoading', true, {root: true});
                //let response = await axios.post('/upload-docs', payload, { crossdomain: true });

                const d = {
                    siteID: payload.siteID,
                    patNum: payload.patNum,
                    name: payload.name,
                    type: payload.type                
                };                

                const json = JSON.stringify(d);
                const signedRequest = await axios.post('/get-form-upload-url', json, {
                    headers: {
                        // Overwrite Axios's automatically set Content-Type
                        'Content-Type': 'application/json'
                    }
                });
                
                //console.log("Signed request url", signedRequest);
                const formData = new FormData();
                
                /**
                 *  Import rule to know here is that ALL the other keys have to be
                 * added first before the "file" key. Apparently, AWS doens't look at
                 * anything after the "file" key.
                 */

                Object.keys(signedRequest.data.fields).forEach(key => {
                    formData.append(key, signedRequest.data.fields[key]);
                });                

                formData.append('file', payload.file);

                /*
                for (var pair of formData.entries()) {
                    console.log(pair[0]+ ', ' + pair[1]); 
                } 
                */             

                const uploadRequest = await axios.post(signedRequest.data.url, formData, {
                    headers: {
                        // Overwrite Axios's automatically set Content-Type
                        'Content-Type': 'multipart/form-data'
                    }
                });                
                
                return uploadRequest;
                //console.dir(uploadRequest);
                //console.log("Response from s3", uploadRequest);
                //dispatch('alert/setLoaded', true, {root: true});
                //dispatch('alert/setMessage', 'Uploaded successfully!', {root: true});
                //dispatch('alert/setDismissSuccessCountDown', 3, {root: true});                
            } catch (err) {
                console.log('Error uploading a document for form:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        // =============================================
        // FORM PACKETS
        // =============================================

        async savePacket({dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/save-packet', payload, { crossdomain: true });
                invalidateSendCatalog('packets');
                dispatch('alert/setLoaded', true, {root: true});
                return response;
            } catch (err) {
                console.log('Error saving packet:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        async getPackets({dispatch}, payload) {
            // payload: { siteID, [silent] }
            //   silent: optional boolean (absent => exactly the old behaviour: alert/* dispatches, and a
            //     failure resolves []). true skips every alert/* dispatch AND a failure resolves
            //     undefined instead of [], so a silent caller (the Send popup's catalog cache and its
            //     prefetch) can tell "failed" from "no packets" and never caches a failure.
            const silent = !!(payload && payload.silent);
            let body = payload;
            if (silent) { body = { ...payload }; delete body.silent; }
            try {
                if (!silent) dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/get-packets', body, { crossdomain: true });
                if (!silent) dispatch('alert/setLoaded', true, {root: true});
                return response.data || [];
            } catch (err) {
                console.log('Error getting packets:', err);
                if (silent) return undefined;
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                return [];
            }
        },

        async getPacket({dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.get(`/get-packet/${payload.packetID}`, { crossdomain: true });
                dispatch('alert/setLoaded', true, {root: true});
                return response;
            } catch (err) {
                console.log('Error getting packet:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async deletePacket({dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post('/delete-packet', payload, { crossdomain: true });
                invalidateSendCatalog('packets');
                dispatch('alert/setLoaded', true, {root: true});
                return response;
            } catch (err) {
                console.log('Error deleting packet:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        async togglePinPacket({dispatch}, payload) {
            try {
                let response = await axios.post('/toggle-pin-packet', payload, { crossdomain: true });
                return response;
            } catch (err) {
                console.log('Error toggling pin packet:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        // payload { siteID, packetID, patNum, linkMode? } — linkMode rides through untouched (generic links only).
        async getPacketUrl({dispatch}, payload) {
            try {
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post('/get-packet-url', payload, { crossdomain: true });
                dispatch('alert/setLoaded2', true, {root: true});
                return response;
            } catch (err) {
                console.log('Error getting packet url:', err);
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw err;
            }
        },

        async translateFormConfig({ dispatch }, payload) {
            try {
                dispatch('alert/setLoading', true, { root: true });
                let response = await axios.post('/translate-form-config', payload, { crossdomain: true });
                dispatch('alert/setLoaded', true, { root: true });
                return response;
            } catch (err) {
                console.log('Error translating form config:', err);
                dispatch('alert/setErrored', true, { root: true });
                dispatch('alert/setMessage', extractApiError(err, 'Translation failed'), { root: true });
                throw err;
            }
        },

        async captureFormLogs({ dispatch }, logPayload) {
            try {
                dispatch('alert/setLoading', true, { root: true });
                console.log('[captureFormLogs] Sending JSON payload:', logPayload);
                // Also echo error-level entries directly to console so they are not hidden
                try {
                    if (logPayload && Array.isArray(logPayload.logs)) {
                        logPayload.logs.forEach(entry => {
                            if (entry && entry.level === 'error') {
                                const msg = entry.message || '[no message]';
                                console.error('[captureFormLogs] DIRECT ERROR (pre-send):', msg);
                                if (entry.stack) console.error('[captureFormLogs] STACK (pre-send):', entry.stack);
                                if (entry.info) console.error('[captureFormLogs] CONTEXT (pre-send):', entry.info);
                            }
                        });
                    }
                } catch (_) { /* ignore logging issues */ }
                const axiosConfig = {
                    baseURL: axios.defaults.baseURL,
                    headers: { 'Content-Type': 'application/json' },
                    crossdomain: true
                };
                let response = await axios.post('/capture-form-logs', logPayload, axiosConfig);
                dispatch('alert/setLoaded', true, { root: true });
                return response;
            } catch (err) {
                // Print a clear, direct error to console to avoid hiding important details
                try {
                    const directMsg = err?.response?.data?.error || err?.message || String(err);
                    const directStack = err?.stack || err?.response?.data?.stack || null;
                    console.error('[captureFormLogs] DIRECT ERROR:', directMsg);
                    if (directStack) console.error('[captureFormLogs] STACK:', directStack);
                } catch (loggingError) {
                    // Ignore logging errors to prevent infinite error loops
                    console.warn('[captureFormLogs] Failed to log error details:', loggingError);
                }
                console.log('[captureFormLogs] Error sending captured logs:', err);
                dispatch('alert/setErrored', true, { root: true });
                dispatch('alert/setMessage', err, { root: true });
                throw err;
            }
        },
    }
}